import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Inbox } from "lucide-react";

export default function Approvals() {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [comment, setComment] = useState({});

  const load = () => api.get("/requisitions?status=pending_approval").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);

  const myTurn = (r) => {
    const step = r.approval_chain?.[r.current_step - 1];
    return step && (step.approver_role === user.role || user.role === "super_admin");
  };

  const act = async (id, action) => {
    try {
      await api.post(`/requisitions/${id}/approve`, { action, comment: comment[id] || "" });
      toast.success(`Requisition ${action}d`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const mine = reqs.filter(myTurn);
  const others = reqs.filter((r) => !myTurn(r));

  return (
    <div data-testid="approvals-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Governance</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">Approvals Inbox</h1>
      <p className="text-sm text-muted-foreground mt-1">{mine.length} requisition(s) awaiting your decision.</p>

      <div className="space-y-3 mt-6">
        {mine.map((r) => {
          const step = r.approval_chain[r.current_step - 1];
          return (
            <div key={r.id} data-testid={`approval-item-${r.id}`} className="border-2 border-[#0A251D] bg-white rounded-xl p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <Link to={`/requisitions/${r.id}`} className="font-heading font-bold text-lg tracking-tight hover:text-[#E05A47] transition-colors duration-100">{r.title}</Link>
                  <div className="text-xs text-muted-foreground mt-1">
                    Step {r.current_step}/{r.approval_chain.length}: <span className="font-bold text-[#0A251D]">{step.label}</span> · {r.openings} opening(s) · {r.experience_min}–{r.experience_max} yrs · Priority {r.priority} · by {r.created_by}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input data-testid={`approval-comment-${r.id}`} value={comment[r.id] || ""} onChange={(e) => setComment({ ...comment, [r.id]: e.target.value })}
                    placeholder="Comment (optional)" className="border px-3 py-2 text-sm w-44" />
                  <button data-testid={`inbox-approve-${r.id}`} onClick={() => act(r.id, "approve")}
                    className="flex items-center gap-1.5 bg-[#0A251D] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150"><CheckCircle2 size={13} /> Approve</button>
                  <button data-testid={`inbox-reject-${r.id}`} onClick={() => act(r.id, "reject")}
                    className="flex items-center gap-1.5 bg-[#E05A47] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150"><XCircle size={13} /> Reject</button>
                </div>
              </div>
            </div>
          );
        })}
        {mine.length === 0 && (
          <div className="border bg-white rounded-xl p-10 text-center">
            <Inbox size={28} className="mx-auto text-muted-foreground" />
            <div className="text-sm text-muted-foreground mt-2">Nothing waiting on you. All clear!</div>
          </div>
        )}
      </div>

      {others.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mt-8 mb-3">In approval with other roles ({others.length})</div>
          {others.map((r) => (
            <div key={r.id} className="border bg-white rounded-xl p-4 mb-2 flex items-center justify-between text-sm">
              <Link to={`/requisitions/${r.id}`} className="font-semibold hover:text-[#E05A47] transition-colors duration-100">{r.title}</Link>
              <span className="text-xs text-muted-foreground capitalize">waiting on {r.approval_chain[r.current_step - 1]?.approver_role.replace(/_/g, " ")}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
